// @contexts/ContentClient.context.tsx
import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import * as Option from "@utils/Option";
import type { OptionTs } from "@ps-bridge/interop";
import type { ContentClient } from '@services/clients/ContentClient';
import { fromInteropContentClient } from '@services/clients/ContentClient';
import { createDefaultContentClient } from "@src/purescript/interop";
import { StubContentClient } from '@services/stubs/StubContentClient';
import { enabledFeatures, FeaturesEnum } from '@src/utils/enabledFeatures';
import { useJwtController } from '@contexts/Jwt.context';
import { requireContext } from '@contexts/requireContext';

const ContentClientContext = createContext<OptionTs<ContentClient>>(Option.none());

export const ContentClientProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { markUnauthorized } = useJwtController();
    // Create the content client, conditionally using the StubContentClient if the flag is set
    const contentClient = useMemo<ContentClient>(() => {
        if (enabledFeatures[FeaturesEnum.StubClients]) {
            return new StubContentClient();
        }
        return fromInteropContentClient(createDefaultContentClient(markUnauthorized));
    }, [markUnauthorized]);

    const contextValue = useMemo(
        () => Option.some(contentClient),
        [contentClient]
    );

    return (
        <ContentClientContext.Provider value={contextValue}>
            {children}
        </ContentClientContext.Provider>
    );
};

export const useContentClient = (): ContentClient => {
    return requireContext(
        useContext(ContentClientContext),
        'useContentClient must be used within a ContentClientProvider',
    );
};
